import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { X, User, UserPlus, UserCheck } from 'lucide-react';
import { Money } from '../contexts/CurrencyContext';

export const AuthPromptModal = ({ 
  onClose, 
  onSignIn, 
  onSignUp, 
  onContinueAsGuest,
  flightDetails 
}) => {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" data-testid="auth-prompt-modal">
      <Card className="w-full max-w-md bg-white shadow-2xl relative overflow-hidden border-0 animate-in fade-in zoom-in-95 duration-200">
        {/* Colorful top border */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-brand-500 via-purple-500 to-cyan-500"></div>
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 transition-colors z-10"
          aria-label="Close"
          data-testid="auth-prompt-close"
        >
          <X className="h-5 w-5 text-slate-500" />
        </button>
        
        <CardHeader className="text-center pb-2 pt-8 px-6">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-gradient-to-br from-brand-500 via-purple-500 to-blue-500 rounded-2xl shadow-xl">
              <User className="h-10 w-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900">
            Sign In to Continue
          </CardTitle>
          <CardDescription className="text-sm mt-2 text-slate-500">
            Sign in to your account to complete your booking or continue as a guest
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4 px-6 pb-6">
          {/* Flight Summary */}
          {flightDetails && (
            <div className="bg-slate-50 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Selected Flight</span>
                <span className="font-bold text-brand-600"><Money gbp={Math.round(flightDetails.price)} /></span>
              </div>
              <div className="text-lg font-semibold text-slate-800 mt-1">
                {flightDetails.airline} • {flightDetails.from} → {flightDetails.to}
              </div>
            </div>
          )}
          
          {/* Sign In Button */}
          <Button 
            onClick={onSignIn}
            className="w-full h-12 bg-gradient-to-r from-brand-500 via-purple-500 to-blue-500 hover:from-brand-600 hover:via-purple-600 hover:to-blue-600 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
            data-testid="auth-prompt-signin"
          >
            <User className="h-5 w-5 mr-2" />
            Sign In
          </Button>
          
          {/* Sign Up Button */}
          <Button 
            onClick={onSignUp}
            variant="outline"
            className="w-full h-12 border-2 border-brand-200 hover:border-brand-400 hover:bg-brand-50 text-brand-700 text-base font-semibold rounded-xl transition-all"
            data-testid="auth-prompt-signup"
          >
            <UserPlus className="h-5 w-5 mr-2" />
            Create Account
          </Button>
          
          {/* Divider */}
          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-3 text-slate-400">or</span>
            </div>
          </div>
          
          {/* Continue as Guest Button */}
          <Button 
            onClick={onContinueAsGuest}
            variant="ghost"
            className="w-full h-12 text-slate-600 hover:text-slate-800 hover:bg-slate-100 text-base font-medium rounded-xl transition-all"
            data-testid="auth-prompt-guest"
          >
            <UserCheck className="h-5 w-5 mr-2" />
            Continue as Guest
          </Button>
          
          {/* Info text */}
          <p className="text-xs text-center text-slate-400 mt-2">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
